import {
  DEFAULT_COMPANY_IMAGE_SECTION_MAP,
  DEFAULT_COMPANY_IMAGE_SECTIONS,
  DEFAULT_FAQ_KNOWLEDGE_ITEMS,
  MAX_COMPANY_IMAGE_ITEMS,
  MAX_FAQ_ANSWER_LENGTH,
  MAX_FAQ_KNOWLEDGE_ITEMS,
  MAX_FAQ_QUESTION_LENGTH
} from "./Config";
import enTexts from "../../../i18n/en";
import zhTexts from "../../../i18n/zh";

const COMPANY_IMAGE_SECTION_KEY_ALIASES = {
  companyOverview: "companyOverview",
  competitiveAdvantages: "competitiveAdvantages",
  customizationCapabilities: "customizationCapabilities",
  extendedCompanyProfile: "extendedCompanyProfile",
  packagingShipping: "packagingShipping",
  factoryProfile: "factoryProfile",
  productionWorkflow: "productionWorkflow",
  tradeShow: "tradeShow",
  companyimagesectionoverview: "companyOverview",
  companyimagesectionadvantages: "competitiveAdvantages",
  companyimagesectioncustomization: "customizationCapabilities",
  companyimagesectionextended: "extendedCompanyProfile",
  companyimagesectionpackaging: "packagingShipping",
  companyimagesectionfactory: "factoryProfile",
  companyimagesectionworkflow: "productionWorkflow",
  companyimagesectiontradeshow: "tradeShow",
  "company overview": "companyOverview",
  "competitive advantages": "competitiveAdvantages",
  "customization capabilities": "customizationCapabilities",
  "extended company profile": "extendedCompanyProfile",
  "packaging & shipping specifications": "packagingShipping",
  "factory profile": "factoryProfile",
  "production workflow": "productionWorkflow",
  "trade show participation": "tradeShow"
};

function resolveAdminLabelByLocale(value, locale, fallback = "") {
  const raw = String(value || "").trim();
  if (!raw) {
    return String(fallback || "").trim();
  }

  const languagePack = locale === "zh" ? zhTexts : enTexts;
  const translated = languagePack?.admin?.[raw];
  if (typeof translated === "string" && translated.trim()) {
    return translated.trim();
  }

  return raw;
}

function toCanonicalCompanyImageSectionKey(rawKey) {
  const key = String(rawKey || "").trim();
  if (!key) {
    return "";
  }

  const direct = COMPANY_IMAGE_SECTION_KEY_ALIASES[key];
  if (direct) {
    return direct;
  }

  const lowered = key.toLowerCase();
  const loweredAlias = COMPANY_IMAGE_SECTION_KEY_ALIASES[lowered];
  if (loweredAlias) {
    return loweredAlias;
  }

  return key;
}

export function readStoredList(storageKey, fallbackRows) {
  if (typeof window === "undefined") {
    return fallbackRows;
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return fallbackRows;
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return fallbackRows;
    }

    const storedRows = parsed.filter((item) => item && typeof item === "object");
    const fallbackByKey = new Map(
      fallbackRows.map((item) => [String(item?.key || "").trim(), item])
    );
    const normalizedStoredRows = storedRows.map((item) => {
      const key = String(item?.key || "").trim();
      const fallback = fallbackByKey.get(key);
      if (!fallback) {
        return item;
      }

      const fallbackPermissions = Array.isArray(fallback.permissions) ? fallback.permissions : [];
      const currentPermissions = Array.isArray(item.permissions) ? item.permissions : [];
      const mergedPermissions = Array.from(new Set([...currentPermissions, ...fallbackPermissions]));

      return {
        ...item,
        permissions: mergedPermissions
      };
    });
    const storedKeys = new Set(
      normalizedStoredRows
        .map((item) => String(item?.key || "").trim())
        .filter(Boolean)
    );
    const mergedRows = [...normalizedStoredRows];

    fallbackRows.forEach((item) => {
      const key = String(item?.key || "").trim();
      if (!key || storedKeys.has(key)) {
        return;
      }

      mergedRows.push(item);
      storedKeys.add(key);
    });

    return mergedRows;
  } catch {
    return fallbackRows;
  }
}

export function readStoredJson(storageKey, fallbackValue) {
  if (typeof window === "undefined") {
    return fallbackValue;
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return fallbackValue;
    }

    return JSON.parse(raw);
  } catch {
    return fallbackValue;
  }
}

export function inferPositionGroup(profileData, role) {
  const raw = String(
    profileData?.positionGroup ||
      profileData?.permissionGroup ||
      profileData?.postGroup ||
      profileData?.position ||
      ""
  )
    .trim()
    .toLowerCase();

  if (/游客|guest|visitor/.test(raw) || role === "guest") {
    return "guest";
  }
  if (/客户|customer|client|buyer/.test(raw) || role === "customer") {
    return "customer";
  }
  return "internal";
}

export function normalizePositionScopeKey(value) {
  const raw = String(value || "")
    .trim()
    .toLowerCase();

  if (!raw) {
    return "";
  }

  const aliases = {
    "运营管理员": "ops-admin",
    "ops-admin": "ops-admin",
    "运营专员": "operations-specialist",
    "operations-specialist": "operations-specialist",
    "主管": "supervisor",
    "supervisor": "supervisor",
    "选品专员": "merchandise-selection-specialist",
    "merchandise-selection-specialist": "merchandise-selection-specialist",
    "选品采购专员": "merchandising-procurement-specialist",
    "merchandising-procurement-specialist": "merchandising-procurement-specialist",
    "采购专员": "sourcing-specialist",
    "sourcing-specialist": "sourcing-specialist",
    "外贸跟单": "merchandiser",
    "merchandiser": "merchandiser",
    "物流专员": "logistics-specialist",
    "logistics-specialist": "logistics-specialist",
    "外贸销售": "sales-rep",
    "sales-rep": "sales-rep",
    "销售经理": "sales-manager",
    "sales-manager": "sales-manager",
    "业务员": "sales-executive",
    "sales-executive": "sales-executive",
    "财务专员": "finance-specialist",
    "finance-specialist": "finance-specialist",
    "财务经理": "finance-manager",
    "finance-manager": "finance-manager",
    "客服专员": "service-agent",
    "service-agent": "service-agent",
    "营销专员": "marketing-specialist",
    "marketing-specialist": "marketing-specialist",
    "人事专员": "hr-specialist",
    "hr-specialist": "hr-specialist",
    "人力行政专员": "hr-admin-specialist",
    "人职行政专干": "hr-admin-specialist",
    "hr-admin-specialist": "hr-admin-specialist",
    "it管理员": "it-admin",
    "信息管理员": "it-admin",
    "it-admin": "it-admin",
    "总经理": "general-manager",
    "general-manager": "general-manager"
  };

  if (aliases[raw]) {
    return aliases[raw];
  }

  if (["运营专员", "operations-specialist"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "operations-specialist";
  }

  if (["选品采购专员", "merchandising-procurement-specialist"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "merchandising-procurement-specialist";
  }

  if (["业务员", "sales-executive"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "sales-executive";
  }

  if (["人力行政专员", "人职行政专干", "hr-admin-specialist"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "hr-admin-specialist";
  }

  if (["运营管理员", "ops-admin"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "ops-admin";
  }

  if (["采购专员", "sourcing-specialist"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "sourcing-specialist";
  }

  if (["外贸销售", "sales-rep"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "sales-rep";
  }

  if (["销售经理", "sales-manager"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "sales-manager";
  }

  if (["财务专员", "finance-specialist"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "finance-specialist";
  }

  if (["财务经理", "finance-manager"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "finance-manager";
  }

  if (["客服专员", "service-agent"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "service-agent";
  }

  if (["营销专员", "marketing-specialist"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "marketing-specialist";
  }

  if (["人事专员", "hr-specialist"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "hr-specialist";
  }

  if (["it管理员", "信息管理员", "it-admin"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "it-admin";
  }

  if (["总经理", "general-manager"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "general-manager";
  }

  if (["主管", "supervisor"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "supervisor";
  }

  if (["选品专员", "merchandise-selection-specialist"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "merchandise-selection-specialist";
  }

  if (["外贸跟单", "merchandiser"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "merchandiser";
  }

  if (["物流专员", "logistics-specialist"].some((item) => raw.includes(String(item).toLowerCase()))) {
    return "logistics-specialist";
  }

  return raw;
}

export function normalizeCapabilities(caps) {
  if (!Array.isArray(caps)) {
    return [];
  }

  return caps
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .sort();
}

export function isSameProfilePermissionState(prev, next) {
  if (!prev && !next) {
    return true;
  }

  if (!prev || !next) {
    return false;
  }

  const prevCapabilities = normalizeCapabilities(prev.capabilities);
  const nextCapabilities = normalizeCapabilities(next.capabilities);

  return (
    String(prev.id || "") === String(next.id || "") &&
    String(prev.role || "") === String(next.role || "") &&
    String(prev.roleLabel || "") === String(next.roleLabel || "") &&
    String(prev.positionGroup || prev.permissionGroup || prev.postGroup || prev.position || "") ===
      String(next.positionGroup || next.permissionGroup || next.postGroup || next.position || "") &&
    prevCapabilities.join("|") === nextCapabilities.join("|")
  );
}

function pathToKey(path) {
  return Array.isArray(path) ? path.join("-") : "";
}

export function collectExpandablePaths(items, parentPath = []) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.flatMap((item, index) => {
    const currentPath = [...parentPath, index];
    if (!Array.isArray(item?.children) || !item.children.length) {
      return [];
    }

    return [pathToKey(currentPath), ...collectExpandablePaths(item.children, currentPath)];
  });
}

export function pickMenusByKey(sourceMenus, keys) {
  const menuMap = new Map((Array.isArray(sourceMenus) ? sourceMenus : []).map((menu) => [menu.key, menu]));
  return keys.map((key) => menuMap.get(key)).filter(Boolean);
}

export function normalizeFaqKnowledgeItems(items, fallbackItems = DEFAULT_FAQ_KNOWLEDGE_ITEMS) {
  const fallback = Array.isArray(fallbackItems) && fallbackItems.length
    ? fallbackItems
    : DEFAULT_FAQ_KNOWLEDGE_ITEMS;

  const fallbackNormalized = fallback
    .map((item, index) => ({
      id: String(item.id || index + 1),
      question: String(item.question || "").trim().slice(0, MAX_FAQ_QUESTION_LENGTH),
      answer: String(item.answer || "").trim().slice(0, MAX_FAQ_ANSWER_LENGTH)
    }))
    .filter((item) => item.question && item.answer)
    .slice(0, MAX_FAQ_KNOWLEDGE_ITEMS);

  if (!Array.isArray(items)) {
    return fallbackNormalized;
  }

  const normalized = items
    .map((item, index) => ({
      id: String(item?.id || index + 1),
      question: String(item?.question || item?.q || "").trim().slice(0, MAX_FAQ_QUESTION_LENGTH),
      answer: String(item?.answer || item?.a || "").trim().slice(0, MAX_FAQ_ANSWER_LENGTH)
    }))
    .filter((item) => item.question || item.answer)
    .slice(0, MAX_FAQ_KNOWLEDGE_ITEMS);

  if (!normalized.length) {
    return fallbackNormalized;
  }

  return normalized;
}

export function buildCompanyImageSectionLabel(section, locale) {
  if (locale === "zh") {
    return resolveAdminLabelByLocale(section?.labelZh || section?.label, "zh", section?.key || "");
  }
  return resolveAdminLabelByLocale(section?.labelEn || section?.label, "en", section?.key || "");
}

export function getCompanyImageSectionLocaleLabels(section) {
  const key = String(section?.key || "").trim();
  const fallback = key ? DEFAULT_COMPANY_IMAGE_SECTION_MAP[key] : null;
  const labelZh = resolveAdminLabelByLocale(section?.labelZh || fallback?.labelZh || section?.label, "zh", key || "");
  const labelEn = resolveAdminLabelByLocale(section?.labelEn || fallback?.labelEn || section?.label, "en", key || "");
  return { labelZh, labelEn };
}

export function normalizeCompanyImageValue(image) {
  return String(image?.path || image?.imagePath || image?.url || image || "").trim();
}

export function createCompanyImageSectionKey(value) {
  const base = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\u4e00-\u9fa5]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 42);
  const seed = base || `custom-${Date.now().toString(36)}`;
  return `custom-${seed}`;
}

export function normalizeCompanyImageSections(items, fallbackItems = DEFAULT_COMPANY_IMAGE_SECTIONS) {
  const mergeImages = (...groups) =>
    Array.from(new Set(groups.flatMap((group) => (Array.isArray(group) ? group : []))))
      .map(normalizeCompanyImageValue)
      .filter(Boolean)
      .slice(0, MAX_COMPANY_IMAGE_ITEMS);

  const fallback = (Array.isArray(fallbackItems) && fallbackItems.length ? fallbackItems : DEFAULT_COMPANY_IMAGE_SECTIONS)
    .map((item) => ({
      key: toCanonicalCompanyImageSectionKey(item?.key),
      labelEn: String(item?.labelEn || item?.label || "").trim(),
      labelZh: String(item?.labelZh || item?.label || "").trim(),
      enabled: item?.enabled !== false,
      images: Array.isArray(item?.images)
        ? item.images.map(normalizeCompanyImageValue).filter(Boolean).slice(0, MAX_COMPANY_IMAGE_ITEMS)
        : []
    }))
    .filter((item) => item.key);

  if (!Array.isArray(items)) {
    return fallback;
  }

  const sourceMap = items.reduce((result, item) => {
    if (!item || typeof item !== "object") {
      return result;
    }

    const key = toCanonicalCompanyImageSectionKey(item.key);
    if (!key) {
      return result;
    }

    const defaults = DEFAULT_COMPANY_IMAGE_SECTION_MAP[key] || null;
    const rawLabel = String(item.label || "").trim();
    const nextValue = {
      key,
      labelEn: String(item.labelEn || defaults?.labelEn || rawLabel).trim(),
      labelZh: String(item.labelZh || defaults?.labelZh || rawLabel).trim(),
      enabled: item.enabled !== false,
      images: Array.isArray(item.images)
        ? item.images.map(normalizeCompanyImageValue).filter(Boolean).slice(0, MAX_COMPANY_IMAGE_ITEMS)
        : []
    };

    const prevValue = result.get(key);
    if (!prevValue) {
      result.set(key, nextValue);
      return result;
    }

    result.set(key, {
      key,
      labelEn: nextValue.labelEn || prevValue.labelEn,
      labelZh: nextValue.labelZh || prevValue.labelZh,
      enabled: prevValue.enabled || nextValue.enabled,
      images: mergeImages(prevValue.images, nextValue.images)
    });

    return result;
  }, new Map());

  const deduplicatedFallback = Array.from(
    fallback.reduce((result, item) => {
      if (!result.has(item.key)) {
        result.set(item.key, item);
      }
      return result;
    }, new Map()).values()
  );

  const merged = deduplicatedFallback.map((item) => {
    const matched = sourceMap.get(item.key);
    if (!matched) {
      return item;
    }

    return {
      ...item,
      labelEn: matched.labelEn || item.labelEn,
      labelZh: matched.labelZh || item.labelZh,
      enabled: matched.enabled,
      images: matched.images.length ? matched.images : item.images
    };
  });

  const fallbackKeySet = new Set(deduplicatedFallback.map((item) => item.key));
  const extras = Array.from(sourceMap.values()).filter((item) => !fallbackKeySet.has(item.key));

  return [...merged, ...extras];
}

export function normalizeExternalLink(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }

  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  if (/^\/\//.test(raw)) {
    return `https:${raw}`;
  }

  return `https://${raw}`;
}